import React, { useState, FormEvent, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { ExpenseService } from "services/expense.service";
import { FundService } from "services/fund.service";
import UserService from "services/user.service";
import { ISaveExpense, IFundResponse } from "models/church.types";

export default function ExpenseRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [funds, setFunds] = useState<IFundResponse[]>([]);

  const [expense, setExpense] = useState<ISaveExpense>({
    title: "",
    amount: 0,
    expenseDate: new Date().toISOString(),
    category: "",
    description: "",
    approvedById: null,
    fundId: null,
  });

  useEffect(() => {
    const initForm = async () => {
      setLoadingFunds(true);
      try {
        // 1. Chargement de la liste des caisses disponibles
        const fundsResult = await FundService.getAll(0, 100);
        setFunds(fundsResult.data || []);

        if (isEditMode && id) {
          // MODE ÉDITION : Récupération des détails de la dépense
          const data = await ExpenseService.getById(parseInt(id, 10));
          setExpense({
            ...data,
            fundId: data.fundId ?? null,
          });
        } else {
          // MODE CRÉATION : Pré-remplissage avec l'utilisateur connecté
          const userRes = await UserService.getCurrentAccount();
          if (userRes.data && userRes.data.id) {
            setExpense((prev) => ({ ...prev, approvedById: userRes.data.id }));
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données :", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoadingFunds(false);
      }
    };
    initForm();
  }, [id, isEditMode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode) {
        // En mode édition, transmission de l'ID
        await ExpenseService.update({ ...expense, id: parseInt(id, 10) } as any);
        toast.success("Dépense mise à jour avec succès");
      } else {
        await ExpenseService.create(expense);
        toast.success("Dépense enregistrée avec succès");
      }
      history.push("/admin/expenses");
    } catch (error) {
      console.error("Erreur d'enregistrement :", error);
      toast.error("Erreur lors de l'enregistrement de la dépense.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-start md:justify-center mt-14 p-4 w-full min-h-screen py-12 md:py-6">
      <div className="w-full max-w-2xl">
        <div className="bg-[#1E293B] border border-slate-700 shadow-2xl rounded-xl overflow-hidden">
          {/* HEADER */}
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 flex justify-between items-center">
            <h6 className="text-white text-sm font-bold uppercase tracking-widest">
              <i className="fas fa-receipt mr-2 text-[#D4AF37]"></i>
              {isEditMode ? "Modifier la dépense" : "Nouvelle Dépense"}
            </h6>
            <button
              type="button"
              onClick={() => history.goBack()}
              className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
            >
              <i className="fas fa-times mr-1"></i> Fermer
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="px-6 py-8 grid grid-cols-1 gap-6"
          >
            {/* Titre */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Titre de la dépense *
              </label>
              <input
                type="text"
                required
                value={expense.title}
                onChange={(e) =>
                  setExpense({ ...expense, title: e.target.value })
                }
                placeholder="Ex: Achat fournitures de bureau, Facture électricité..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all"
              />
            </div>

            {/* Caisse à débiter */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                <i className="fas fa-wallet mr-2 text-[#D4AF37]"></i>Caisse source (À débiter)
              </label>
              <div className="relative">
                <select
                  name="fundId"
                  value={expense.fundId ?? ""}
                  onChange={(e) =>
                    setExpense({
                      ...expense,
                      fundId: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  disabled={loadingFunds}
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                >
                  <option value="">-- Aucune caisse (Non rattachée) --</option>
                  {funds.map((fund) => (
                    <option key={fund.id} value={fund.id}>
                      {fund.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                  {loadingFunds ? (
                    <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
                  ) : (
                    <i className="fas fa-chevron-down text-xs"></i>
                  )}
                </div>
              </div>
              <p className="text-slate-500 text-[11px] mt-1 italic ml-1">
                Le montant sera déduit du solde de la caisse sélectionnée.
              </p>
            </div>

            {/* Montant & Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                  Montant (XOF) *
                </label>
                <input
                  type="number"
                  required
                  value={expense.amount === 0 ? "" : expense.amount}
                  onChange={(e) =>
                    setExpense({ ...expense, amount: Number(e.target.value) })
                  }
                  placeholder="0"
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                  Date de la dépense *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={
                    expense.expenseDate ? expense.expenseDate.slice(0, 16) : ""
                  }
                  onChange={(e) =>
                    setExpense({
                      ...expense,
                      expenseDate: new Date(e.target.value).toISOString(),
                    })
                  }
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Catégorie */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Catégorie
              </label>
              <input
                type="text"
                placeholder="Ex: Électricité, Transport, Maintenance, Logistique..."
                value={expense.category || ""}
                onChange={(e) =>
                  setExpense({ ...expense, category: e.target.value })
                }
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Description / Motif
              </label>
              <textarea
                value={expense.description || ""}
                onChange={(e) =>
                  setExpense({ ...expense, description: e.target.value })
                }
                placeholder="Détails complémentaires concernant cette sortie de caisse..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none h-24 transition-all"
              />
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
              <button
                type="button"
                onClick={() => history.push("/admin/expenses")}
                className="px-6 py-3 text-slate-400 hover:text-white font-bold text-xs uppercase transition-all"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner animate-spin mr-2"></i> Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    {isEditMode ? "Mettre à jour" : "Enregistrer"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}